import { useEffect, useMemo, useRef, useState } from "react";
import { formatEmploymentType } from "@/lib/employmentType";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Users, AlertCircle, BarChart3, PieChart,
  Cake, Search, Mail, Phone, MapPin, Building2, Trophy, Heart,
  Clock, TrendingUp, TrendingDown, Download, Ticket, Eye, MessageSquare,
  UserPlus, UserMinus, CalendarDays, Grid3X3, List, Filter, ChevronRight, ChevronDown, Target
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, differenceInYears, differenceInDays, startOfDay } from "date-fns";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { countByDepartment, departmentLabel } from "@/lib/departmentLabel";
import { buildDistribution, formatHeadcountBreakdown, getEmployeeHeadcount, isCountedInHeadcount } from "@/lib/employeeHeadcount";
import { cn } from "@/lib/utils";
import { Employee } from "@shared/schema";
import ClientPayrollSettings from "@/components/payroll/ClientPayrollSettings";

interface HRTabProps {
  clientId: number;
  employees: Employee[];
  employeesLoading?: boolean;
}

export default function HRTab({ clientId, employees, employeesLoading }: HRTabProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [directoryView, setDirectoryView] = useState<"grid" | "list">("grid");
  const [orgChartView, setOrgChartView] = useState<"department" | "tree">("department");
  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null);
  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [anniversaryRange, setAnniversaryRange] = useState<"this-month" | "30" | "60" | "90">("this-month");
  const [birthdayRange, setBirthdayRange] = useState<"this-month" | "30" | "60" | "90">("this-month");
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketStatusFilter, setTicketStatusFilter] = useState("all");
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [isTicketDialogOpen, setIsTicketDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: orgLocations = [] } = useQuery<any[]>({
    queryKey: ["/api/org/locations", { ownerType: "client", ownerId: clientId }],
    queryFn: async () => {
      const res = await fetch(`/api/org/locations?ownerType=client&ownerId=${clientId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch locations");
      return res.json();
    },
    enabled: !!clientId,
  });

  const { data: ticketsData, isLoading: ticketsLoading } = useQuery<{ tasks: any[]; pagination?: any }>({
    queryKey: ["/api/tasks", { clientId, q: ticketSearch || undefined, limit: 25 }],
    queryFn: async ({ queryKey }) => {
      const [_k, params] = queryKey as [string, any];
      const url = new URL("/api/tasks", window.location.origin);
      Object.entries(params).forEach(([k, v]) => { if (v) url.searchParams.set(k, String(v)); });
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load tasks");
      return res.json();
    },
    enabled: !!clientId,
  });

  const { data: ticketDetail, refetch: refetchTicketDetail, isFetching: fetchingTicketDetail } = useQuery<any>({
    queryKey: selectedTicketId ? ["/api/tasks", selectedTicketId] : ["/api/tasks/idle"],
    enabled: !!selectedTicketId && isTicketDialogOpen,
    queryFn: async () => {
      const res = await fetch(`/api/tasks/${selectedTicketId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load task detail");
      return res.json();
    }
  });

  const addCommentMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!selectedTicketId) return;
      const res = await apiRequest('POST', `/api/tasks/${selectedTicketId}/comments`, {
        content,
        authorName: 'Broker',
        authorEmail: 'broker@agency.com',
        authorType: 'broker'
      });
      return res.json();
    },
    onSuccess: async () => {
      await refetchTicketDetail();
      await queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      toast({ title: "Comment added", variant: "success" });
    },
    onError: () => {
      toast({ title: "Failed to add comment", variant: "destructive" });
    }
  });

  const normalizedEmployees = useMemo(() => {
    return employees.map((e: any) => ({
      ...e,
      hireDate: e.hireDate ? new Date(e.hireDate) : null,
      dateOfBirth: e.dateOfBirth ? new Date(e.dateOfBirth) : null,
      lastReviewDate: e.lastReviewDate ? new Date(e.lastReviewDate) : null,
      terminationDate: e.terminationDate ? new Date(e.terminationDate) : null,
    }));
  }, [employees]);

  const sortedEmployees = useMemo(() => {
    return sortEmployeesByFullName(normalizedEmployees);
  }, [normalizedEmployees]);

  const enrichedEmployees = useMemo(() => {
    const today = new Date();
    return sortedEmployees.map(e => {
      const yearsOfService = e.hireDate ? differenceInYears(today, e.hireDate) : 0;
      return {
        ...e,
        yearsOfService,
        performanceRating: e.performanceRating || 'Unrated',
        skills: Array.isArray(e.skills) ? e.skills : [],
        employeeType: e.employmentType || 'Unknown'
      };
    });
  }, [sortedEmployees]);

  // Headcount = active + pending activation, same as the header (DEV-329).
  const activeEmployees = enrichedEmployees.filter(isCountedInHeadcount);
  const headcount = getEmployeeHeadcount(activeEmployees);
  const totalEmployees = headcount.total;
  const fullTimeEmployees = activeEmployees.filter(emp => /full/i.test(emp.employmentType || '')).length;
  const partTimeEmployees = activeEmployees.filter(emp => /part/i.test(emp.employmentType || '')).length;
  const contractEmployees = activeEmployees.filter(emp => /contract/i.test(emp.employmentType || '')).length;
  const typeNotSetEmployees = activeEmployees.filter(emp => !(emp.employmentType || '').trim()).length;
  
  const averageYearsOfService = totalEmployees > 0
    ? activeEmployees.reduce((sum, emp) => sum + emp.yearsOfService, 0) / totalEmployees
    : 0;

  const numericSalary = (e: any) => {
    const raw = e.compensationAmount || e.salary;
    if (!raw) return 0;
    const num = Number(String(raw).replace(/[^0-9.]/g, ''));
    return Number.isFinite(num) ? num : 0;
  };
  
  const averageSalary = totalEmployees > 0
    ? activeEmployees.reduce((sum, emp) => sum + numericSalary(emp), 0) / totalEmployees
    : 0;

  const oneYearAgo = useMemo(() => new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), []);
  const thirtyDaysAgo = useMemo(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), []);
  const ninetyDaysAgo = useMemo(() => new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), []);
  
  const terminatedLastYear = enrichedEmployees.filter(e => e.terminationDate && e.terminationDate > oneYearAgo).length;
  const turnoverRate = totalEmployees > 0 ? (terminatedLastYear / (totalEmployees + terminatedLastYear)) * 100 : 0;
  const retentionRate = totalEmployees > 0 ? 100 - turnoverRate : 0;

  const newHiresLast30Days = activeEmployees.filter(e => e.hireDate && e.hireDate > thirtyDaysAgo).length;
  const newHiresLast90Days = activeEmployees.filter(e => e.hireDate && e.hireDate > ninetyDaysAgo).length;
  const terminationsLast30Days = enrichedEmployees.filter(e => e.terminationDate && e.terminationDate > thirtyDaysAgo).length;

  const { previousRetention, retentionDelta } = useMemo(() => {
    if (enrichedEmployees.length === 0) return { previousRetention: null, retentionDelta: null };
    const now = new Date();
    const prevYear = now.getFullYear() - 1;
    const periodStart = new Date(prevYear, 0, 1);
    const periodEnd = new Date(prevYear, 11, 31, 23, 59, 59, 999);
    const startingCohort = enrichedEmployees.filter(e => {
      const hireOk = !e.hireDate || e.hireDate <= periodStart;
      const notTerminatedBefore = !e.terminationDate || e.terminationDate >= periodStart;
      return hireOk && notTerminatedBefore;
    });
    const survived = startingCohort.filter(e => !e.terminationDate || e.terminationDate > periodEnd);
    const prevRetention = startingCohort.length > 0 ? (survived.length / startingCohort.length) * 100 : null;
    const delta = prevRetention != null ? retentionRate - prevRetention : null;
    return { previousRetention: prevRetention, retentionDelta: delta };
  }, [enrichedEmployees, retentionRate]);

  // Headcount population, so Department Distribution %s sum to 100 (DEV-359).
  const departmentCounts = countByDepartment(activeEmployees);

  const today = startOfDay(new Date());
  const thisMonth = today.getMonth();
  const thisYear = today.getFullYear();

  const getUpcomingDates = (dateField: 'hireDate' | 'dateOfBirth', range: "this-month" | "30" | "60" | "90") => {
    return enrichedEmployees.filter(emp => {
      const date = emp[dateField];
      if (!date) return false;
      
      if (range === "this-month") {
        return date.getMonth() === thisMonth;
      }
      
      const daysAhead = parseInt(range);
      const thisYearDate = new Date(thisYear, date.getMonth(), date.getDate());
      const nextYearDate = new Date(thisYear + 1, date.getMonth(), date.getDate());
      
      const upcomingDate = thisYearDate >= today ? thisYearDate : nextYearDate;
      const daysUntil = differenceInDays(upcomingDate, today);
      
      return daysUntil >= 0 && daysUntil <= daysAhead;
    }).sort((a, b) => {
      const aDate = a[dateField]!;
      const bDate = b[dateField]!;
      
      if (range === "this-month") {
        return aDate.getDate() - bDate.getDate();
      }
      
      const aThisYear = new Date(thisYear, aDate.getMonth(), aDate.getDate());
      const bThisYear = new Date(thisYear, bDate.getMonth(), bDate.getDate());
      const aUpcoming = aThisYear >= today ? aThisYear : new Date(thisYear + 1, aDate.getMonth(), aDate.getDate());
      const bUpcoming = bThisYear >= today ? bThisYear : new Date(thisYear + 1, bDate.getMonth(), bDate.getDate());
      
      return aUpcoming.getTime() - bUpcoming.getTime();
    });
  };

  const upcomingAnniversaries = getUpcomingDates('hireDate', anniversaryRange);
  const upcomingBirthdays = getUpcomingDates('dateOfBirth', birthdayRange);

  const getMilestoneLabel = (years: number) => {
    if (years >= 20) return { label: "20+ Years", color: "bg-yellow-500 text-white", icon: "🏆" };
    if (years >= 15) return { label: "15 Years", color: "bg-purple-500 text-white", icon: "💎" };
    if (years >= 10) return { label: "10 Years", color: "bg-blue-500 text-white", icon: "⭐" };
    if (years >= 5) return { label: "5 Years", color: "bg-green-500 text-white", icon: "🎯" };
    return null;
  };

  const filteredEmployees = enrichedEmployees.filter(emp => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = [emp.firstName, emp.lastName, emp.department, emp.position]
      .filter(Boolean)
      .some(v => v.toLowerCase().includes(term));
    const matchesDepartment = departmentFilter === 'all' || departmentLabel(emp.department) === departmentFilter;
    return matchesSearch && matchesDepartment;
  });

  const orgChart = useMemo(() => {
    const deptMap: Record<string, any[]> = {};
    enrichedEmployees.forEach(emp => {
      const dept = departmentLabel(emp.department);
      if (!deptMap[dept]) {
        deptMap[dept] = [];
      }
      deptMap[dept].push(emp);
    });
    
    Object.keys(deptMap).forEach(dept => {
      deptMap[dept].sort((a, b) => {
        const aIsManager = enrichedEmployees.some(e => e.managerEmployeeId === a.id);
        const bIsManager = enrichedEmployees.some(e => e.managerEmployeeId === b.id);
        if (aIsManager && !bIsManager) return -1;
        if (!aIsManager && bIsManager) return 1;
        return (b.yearsOfService || 0) - (a.yearsOfService || 0);
      });
    });
    
    return deptMap;
  }, [enrichedEmployees]);

  const filteredTickets = useMemo(() => {
    if (!ticketsData?.tasks) return [];
    return ticketsData.tasks.filter(t => {
      if (ticketStatusFilter === "all") return true;
      return String(t.status || "").toLowerCase() === ticketStatusFilter;
    });
  }, [ticketsData?.tasks, ticketStatusFilter]);

  if (employeesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">HR Overview</h3>
          <p className="text-sm text-muted-foreground">Workforce analytics and employee management</p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="text-sm">Viewing as Broker</Badge>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      <Tabs defaultValue="metrics" className="space-y-4">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="metrics">Metrics</TabsTrigger>
          <TabsTrigger value="demographics">Demographics</TabsTrigger>
          <TabsTrigger value="orgchart">Org Chart</TabsTrigger>
          <TabsTrigger value="anniversaries">Anniversaries</TabsTrigger>
          <TabsTrigger value="birthdays">Birthdays</TabsTrigger>
          <TabsTrigger value="directory">Directory</TabsTrigger>
          <TabsTrigger value="payroll">Payroll</TabsTrigger>
        </TabsList>

        {/* Payroll Tab — pay groups + integrations (broker view of employer-side config). */}
        <TabsContent value="payroll" className="space-y-6">
          <ClientPayrollSettings clientId={clientId} />
        </TabsContent>

        {/* Metrics Tab */}
        <TabsContent value="metrics" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Headcount</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent data-testid="hr-total-headcount">
                <div className="text-2xl font-bold">{totalEmployees}</div>
                <div className="text-xs text-muted-foreground mt-1">{formatHeadcountBreakdown(headcount)}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-muted-foreground">{fullTimeEmployees} FT</span>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-muted-foreground">{partTimeEmployees} PT</span>
                  {contractEmployees > 0 && (
                    <>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">{contractEmployees} Contract</span>
                    </>
                  )}
                  {typeNotSetEmployees > 0 && (
                    <>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">{typeNotSetEmployees} Type not set</span>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Retention Rate</CardTitle>
                {retentionDelta != null && retentionDelta >= 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                )}
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{retentionRate.toFixed(1)}%</div>
                <p className="text-xs text-muted-foreground">
                  {retentionDelta == null || !isFinite(retentionDelta) ? '—' : (
                    <span className={retentionDelta >= 0 ? "text-green-600" : "text-red-600"}>
                      {retentionDelta >= 0 ? '+' : ''}{retentionDelta.toFixed(1)}% vs last year
                    </span>
                  )}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">New Hires</CardTitle>
                <UserPlus className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{newHiresLast30Days}</div>
                <p className="text-xs text-muted-foreground">
                  Last 30 days • {newHiresLast90Days} in 90 days
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Turnover</CardTitle>
                <UserMinus className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{turnoverRate.toFixed(1)}%</div>
                <p className="text-xs text-muted-foreground">
                  {terminationsLast30Days} departures last 30 days
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Tenure</CardTitle>
                <Clock className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{averageYearsOfService.toFixed(1)} yrs</div>
                <p className="text-xs text-muted-foreground">Average years of service</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Salary</CardTitle>
                <BarChart3 className="h-4 w-4 text-purple-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">
                  ${averageSalary > 0 ? Math.round(averageSalary / 1000) + 'k' : '—'}
                </div>
                <p className="text-xs text-muted-foreground">Annual compensation</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Departments</CardTitle>
                <Building2 className="h-4 w-4 text-indigo-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-indigo-600">{Object.keys(departmentCounts).length}</div>
                <p className="text-xs text-muted-foreground">Active departments</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Anniversaries</CardTitle>
                <Trophy className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{upcomingAnniversaries.length}</div>
                <p className="text-xs text-muted-foreground">This month</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Performance Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const entries = buildDistribution(activeEmployees, e => e.performanceRating);
                  const colors = ['bg-green-500', 'bg-blue-500', 'bg-yellow-500', 'bg-orange-500', 'bg-gray-400'];
                  return (
                    <div className="space-y-3">
                      {entries.map(({ label: rating, count, pct }, idx) => {
                        return (
                          <div key={rating} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span>{rating}</span>
                              <span className="text-muted-foreground">{count} ({pct.toFixed(0)}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div className={cn("h-2 rounded-full", colors[idx % colors.length])} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                      {entries.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No performance data available</p>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Employment Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const entries = buildDistribution(activeEmployees, e => formatEmploymentType(e.employmentType));
                  const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500'];
                  return (
                    <div className="space-y-3">
                      {entries.map(({ label: type, count, pct }, idx) => {
                        return (
                          <div key={type} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span>{type}</span>
                              <span className="text-muted-foreground">{count} ({pct.toFixed(0)}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div className={cn("h-2 rounded-full", colors[idx % colors.length])} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                      {entries.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">No employment type data</p>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Demographics Tab */}
        <TabsContent value="demographics" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Department Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(departmentCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([dept, count]) => {
                      const percentage = totalEmployees > 0 ? (count / totalEmployees) * 100 : 0;
                      return (
                        <div key={dept} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>{dept}</span>
                            <span className="text-muted-foreground">{count} ({percentage.toFixed(0)}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${percentage}%` }} />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Work Locations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {orgLocations.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No locations configured in Organization settings</p>
                  ) : (
                    orgLocations.map((loc: any) => {
                      const locationLabel = loc.officeName || (loc.city && loc.state ? `${loc.city}, ${loc.state}` : loc.city || loc.state || 'Unnamed');
                      const employeeCount = activeEmployees.filter(e =>
                        e.locationId === loc.id || 
                        (e.city?.toLowerCase() === loc.city?.toLowerCase() && e.state?.toLowerCase() === loc.state?.toLowerCase())
                      ).length;
                      const percentage = totalEmployees > 0 ? (employeeCount / totalEmployees) * 100 : 0;
                      return (
                        <div key={loc.id} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>{locationLabel}</span>
                            <span className="text-muted-foreground">{employeeCount} ({percentage.toFixed(0)}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: `${percentage}%` }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Age Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const buckets: Record<string, number> = { '18-24': 0, '25-34': 0, '35-44': 0, '45-54': 0, '55+': 0 };
                  const withDob = activeEmployees.filter(e => e.dateOfBirth);
                  withDob.forEach(e => {
                    const age = differenceInYears(new Date(), e.dateOfBirth!);
                    if (age < 25) buckets['18-24']++;
                    else if (age < 35) buckets['25-34']++;
                    else if (age < 45) buckets['35-44']++;
                    else if (age < 55) buckets['45-54']++;
                    else buckets['55+']++;
                  });
                  const totalAge = withDob.length || 1;
                  const colors = ['bg-purple-400', 'bg-purple-500', 'bg-purple-600', 'bg-purple-700', 'bg-purple-800'];
                  return (
                    <div className="space-y-3">
                      {Object.entries(buckets).map(([label, count], idx) => {
                        const pct = (count / totalAge) * 100;
                        return (
                          <div key={label} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span>{label} years</span>
                              <span className="text-muted-foreground">{count} ({pct.toFixed(0)}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div className={cn("h-2 rounded-full", colors[idx])} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                      {withDob.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No date of birth data</p>}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tenure Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const buckets: Record<string, number> = { '< 1 year': 0, '1-3 years': 0, '3-5 years': 0, '5-10 years': 0, '10+ years': 0 };
                  const withHire = activeEmployees.filter(e => e.hireDate);
                  withHire.forEach(e => {
                    const y = e.yearsOfService;
                    if (y < 1) buckets['< 1 year']++;
                    else if (y < 3) buckets['1-3 years']++;
                    else if (y < 5) buckets['3-5 years']++;
                    else if (y < 10) buckets['5-10 years']++;
                    else buckets['10+ years']++;
                  });
                  const totalHire = withHire.length || 1;
                  const colors = ['bg-blue-300', 'bg-blue-400', 'bg-blue-500', 'bg-blue-600', 'bg-blue-700'];
                  return (
                    <div className="space-y-3">
                      {Object.entries(buckets).map(([label, count], idx) => {
                        const pct = (count / totalHire) * 100;
                        return (
                          <div key={label} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span>{label}</span>
                              <span className="text-muted-foreground">{count} ({pct.toFixed(0)}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div className={cn("h-2 rounded-full", colors[idx])} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                      {withHire.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No hire date data</p>}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Org Chart Tab */}
        <TabsContent value="orgchart" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Organization Structure
                  </CardTitle>
                  <CardDescription>
                    {orgChartView === "department" 
                      ? "Employees organized by department with managers listed first"
                      : "Hierarchical view showing manager relationships"}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={orgChartView === "department" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setOrgChartView("department")}
                  >
                    <Grid3X3 className="h-4 w-4 mr-2" />
                    Departments
                  </Button>
                  <Button
                    variant={orgChartView === "tree" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setOrgChartView("tree")}
                  >
                    <ChevronRight className="h-4 w-4 mr-2" />
                    Org Tree
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {orgChartView === "department" ? (
                <div className="space-y-6">
                  {Object.entries(orgChart)
                    .sort((a, b) => b[1].length - a[1].length)
                    .map(([department, deptEmployees]) => {
                      const managers = deptEmployees.filter(emp => 
                        enrichedEmployees.some(e => e.managerEmployeeId === emp.id)
                      );
                      
                      return (
                        <div key={department} className="border rounded-lg overflow-hidden">
                          <div className="bg-muted/50 px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Building2 className="h-5 w-5 text-muted-foreground" />
                              <h3 className="font-semibold text-lg">{department}</h3>
                            </div>
                            <Badge variant="secondary">{deptEmployees.length} employees</Badge>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                              {deptEmployees.map((employee: any) => {
                                const isManager = managers.includes(employee);
                                const directReports = enrichedEmployees.filter(e => e.managerEmployeeId === employee.id).length;
                                const milestone = getMilestoneLabel(employee.yearsOfService);
                                
                                return (
                                  <div 
                                    key={employee.id} 
                                    className={cn(
                                      "flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/25 transition-colors cursor-pointer",
                                      isManager && "border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20"
                                    )}
                                    onClick={() => { setSelectedEmployee(employee); setIsEmployeeDialogOpen(true); }}
                                  >
                                    <Avatar className="h-10 w-10">
                                      {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
                                      <AvatarFallback className={isManager ? "bg-blue-100 text-blue-700" : ""}>
                                        {employee.firstName?.[0]}{employee.lastName?.[0]}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium text-sm truncate">
                                          {employee.firstName} {employee.lastName}
                                        </p>
                                        {isManager && (
                                          <Badge variant="outline" className="text-[10px] h-4 px-1 border-blue-300 text-blue-700">
                                            Lead
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-xs text-muted-foreground truncate">{employee.position}</p>
                                      <div className="flex items-center gap-2 mt-1">
                                        {directReports > 0 && (
                                          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                            <Users className="h-3 w-3" /> {directReports}
                                          </span>
                                        )}
                                        {milestone && (
                                          <span className="text-[10px]">{milestone.icon}</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <OrgTreeView employees={enrichedEmployees} onEmployeeClick={(emp) => { setSelectedEmployee(emp); setIsEmployeeDialogOpen(true); }} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Anniversaries Tab */}
        <TabsContent value="anniversaries" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-yellow-500" />
                    Work Anniversaries
                  </CardTitle>
                  <CardDescription>Celebrate employee milestones and tenure</CardDescription>
                </div>
                <Select value={anniversaryRange} onValueChange={(v: any) => setAnniversaryRange(v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="this-month">This Month</SelectItem>
                    <SelectItem value="30">Next 30 Days</SelectItem>
                    <SelectItem value="60">Next 60 Days</SelectItem>
                    <SelectItem value="90">Next 90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {upcomingAnniversaries.length > 0 ? (
                <div className="space-y-3">
                  {upcomingAnniversaries.map((employee: any) => {
                    const milestone = getMilestoneLabel(employee.yearsOfService);
                    const anniversaryDate = new Date(thisYear, employee.hireDate.getMonth(), employee.hireDate.getDate());
                    const daysUntil = differenceInDays(anniversaryDate >= today ? anniversaryDate : new Date(thisYear + 1, employee.hireDate.getMonth(), employee.hireDate.getDate()), today);
                    
                    return (
                      <div 
                        key={employee.id} 
                        className={cn(
                          "flex items-center gap-4 p-4 border rounded-lg",
                          milestone && "border-yellow-200 bg-yellow-50/50 dark:bg-yellow-950/20 dark:border-yellow-800"
                        )}
                      >
                        <Avatar className="h-14 w-14">
                          {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
                          <AvatarFallback className="text-lg">
                            {employee.firstName?.[0]}{employee.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-lg">
                              {employee.firstName} {employee.lastName}
                            </h3>
                            {milestone ? (
                              <Badge className={cn("text-xs", milestone.color)}>
                                {milestone.icon} {milestone.label}
                              </Badge>
                            ) : (
                              <Badge variant="secondary">{employee.yearsOfService} years</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {employee.position} • {departmentLabel(employee.department)}
                          </p>
                          <div className="flex items-center gap-4 mt-1">
                            <p className="text-sm text-blue-600 dark:text-blue-400">
                              <CalendarDays className="h-3.5 w-3.5 inline mr-1" />
                              {format(employee.hireDate, 'MMMM do')}
                            </p>
                            {daysUntil === 0 ? (
                              <Badge variant="default" className="bg-green-500">Today!</Badge>
                            ) : daysUntil > 0 && daysUntil <= 7 ? (
                              <Badge variant="outline" className="text-orange-600 border-orange-300">
                                In {daysUntil} days
                              </Badge>
                            ) : null}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="gap-2">
                          <Mail className="h-4 w-4" />
                          Congratulate
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Trophy className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No work anniversaries in this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Birthdays Tab */}
        <TabsContent value="birthdays" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Cake className="h-5 w-5 text-pink-500" />
                    Employee Birthdays
                  </CardTitle>
                  <CardDescription>Celebrate your team members</CardDescription>
                </div>
                <Select value={birthdayRange} onValueChange={(v: any) => setBirthdayRange(v)}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="this-month">This Month</SelectItem>
                    <SelectItem value="30">Next 30 Days</SelectItem>
                    <SelectItem value="60">Next 60 Days</SelectItem>
                    <SelectItem value="90">Next 90 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {upcomingBirthdays.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {upcomingBirthdays.map((employee: any) => {
                    const birthdayDate = new Date(thisYear, employee.dateOfBirth.getMonth(), employee.dateOfBirth.getDate());
                    const daysUntil = differenceInDays(birthdayDate >= today ? birthdayDate : new Date(thisYear + 1, employee.dateOfBirth.getMonth(), employee.dateOfBirth.getDate()), today);
                    
                    return (
                      <div 
                        key={employee.id} 
                        className={cn(
                          "flex items-center gap-4 p-4 border rounded-lg",
                          daysUntil === 0 && "border-pink-300 bg-pink-50/50 dark:bg-pink-950/20 dark:border-pink-800"
                        )}
                      >
                        <Avatar className="h-12 w-12">
                          {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
                          <AvatarFallback>
                            {employee.firstName?.[0]}{employee.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-semibold">
                            {employee.firstName} {employee.lastName}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {employee.position} • {departmentLabel(employee.department)}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <p className="text-sm text-pink-600 dark:text-pink-400">
                              <Cake className="h-3.5 w-3.5 inline mr-1" />
                              {format(employee.dateOfBirth, 'MMMM do')}
                            </p>
                            {daysUntil === 0 ? (
                              <Badge className="bg-pink-500">🎂 Today!</Badge>
                            ) : daysUntil > 0 && daysUntil <= 7 ? (
                              <Badge variant="outline" className="text-pink-600 border-pink-300">
                                In {daysUntil} days
                              </Badge>
                            ) : null}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="gap-2">
                          <Heart className="h-4 w-4" />
                          Wish
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Cake className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No birthdays in this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Directory Tab */}
        <TabsContent value="directory" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Employee Directory
                  </CardTitle>
                  <CardDescription>{filteredEmployees.length} of {totalEmployees} employees</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={directoryView === "grid" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setDirectoryView("grid")}
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={directoryView === "list" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setDirectoryView("list")}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-6">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, position, or department..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {Object.keys(departmentCounts).filter(dept => dept && dept.trim() !== '').sort().map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {directoryView === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredEmployees.map((employee: any) => (
                    <div 
                      key={employee.id}
                      className="p-4 border rounded-lg hover:bg-muted/25 cursor-pointer transition-colors"
                      onClick={() => { setSelectedEmployee(employee); setIsEmployeeDialogOpen(true); }}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <Avatar>
                          {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
                          <AvatarFallback>
                            {employee.firstName?.[0]}{employee.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">
                            {employee.firstName} {employee.lastName}
                          </h3>
                          <p className="text-sm text-muted-foreground truncate">{employee.position}</p>
                        </div>
                      </div>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-3.5 w-3.5" />
                          <span className="truncate">{departmentLabel(employee.department)}</span>
                        </div>
                        {employee.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-3.5 w-3.5" />
                            <span className="truncate">{employee.email}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5" />
                          <span>{employee.yearsOfService} years</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Position</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Department</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Tenure</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredEmployees.map((employee: any) => (
                        <tr 
                          key={employee.id}
                          className="hover:bg-muted/25 cursor-pointer"
                          onClick={() => { setSelectedEmployee(employee); setIsEmployeeDialogOpen(true); }}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
                                <AvatarFallback className="text-xs">
                                  {employee.firstName?.[0]}{employee.lastName?.[0]}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-sm">
                                {employee.firstName} {employee.lastName}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">{employee.position}</td>
                          <td className="px-4 py-3 text-sm">{departmentLabel(employee.department)}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{employee.email}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {employee.city}{employee.city && employee.state ? ', ' : ''}{employee.state || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm">{employee.yearsOfService} yrs</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {filteredEmployees.length === 0 && (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                  <p className="text-muted-foreground">No employees found matching your criteria</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Support Tab */}
        <TabsContent value="support" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Client Support Tasks
              </CardTitle>
              <CardDescription>
                View and respond to support requests from this client
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <h3 className="font-semibold flex items-center gap-2 text-lg">
                    <Ticket className="h-5 w-5" />
                    Support Tasks
                  </h3>
                  <div className="flex items-center gap-3">
                    <Select value={ticketStatusFilter} onValueChange={setTicketStatusFilter}>
                      <SelectTrigger className="w-36">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="pending_info">Pending Info</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search tasks..."
                        value={ticketSearch}
                        onChange={(e) => setTicketSearch(e.target.value)}
                        className="pl-9 w-64"
                      />
                    </div>
                  </div>
                </div>

                {ticketsLoading ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">Loading tasks...</p>
                  </div>
                ) : !filteredTickets.length ? (
                  <div className="text-center py-12">
                    <Ticket className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">No tasks found for this client</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredTickets.map((t: any) => (
                      <div
                        key={t.id}
                        className="border rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => { setSelectedTicketId(t.id); setIsTicketDialogOpen(true); }}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="space-y-1 flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-xs text-muted-foreground">{t.ticketNumber}</span>
                              {t.priority && (
                                <Badge 
                                  variant="secondary" 
                                  className={cn(
                                    "text-[10px]",
                                    t.priority === 'urgent' && "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
                                    t.priority === 'high' && "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300"
                                  )}
                                >
                                  {String(t.priority).toUpperCase()}
                                </Badge>
                              )}
                              {t.status && (
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-[10px]",
                                    t.status === 'resolved' && "border-green-300 text-green-700",
                                    t.status === 'closed' && "border-gray-300 text-gray-500"
                                  )}
                                >
                                  {String(t.status).replace(/_/g, ' ')}
                                </Badge>
                              )}
                            </div>
                            <p className="font-medium truncate">{t.title}</p>
                            <p className="text-xs text-muted-foreground truncate">{t.description}</p>
                            <div className="flex items-center gap-4 text-[11px] text-muted-foreground flex-wrap">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {format(new Date(t.createdAt), 'MMM d, yyyy')}
                              </span>
                              {t.tags?.length ? (
                                <span className="flex items-center gap-1">
                                  <MessageSquare className="h-3 w-3" />
                                  {t.tags.slice(0, 2).join(', ')}{t.tags.length > 2 && '…'}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); setSelectedTicketId(t.id); setIsTicketDialogOpen(true); }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          <Dialog open={isTicketDialogOpen} onOpenChange={(open) => { setIsTicketDialogOpen(open); if (!open) setSelectedTicketId(null); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {fetchingTicketDetail ? 'Loading Task...' : ticketDetail ? `${ticketDetail.ticketNumber} • ${ticketDetail.title}` : 'Task'}
                </DialogTitle>
              </DialogHeader>
              {ticketDetail && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      {ticketDetail.priority && <Badge variant="secondary">{String(ticketDetail.priority).toUpperCase()}</Badge>}
                      {ticketDetail.status && <Badge variant="outline">{String(ticketDetail.status).replace(/_/g, ' ')}</Badge>}
                      <span className="text-muted-foreground ml-auto">Created {format(new Date(ticketDetail.createdAt), 'PP p')}</span>
                    </div>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{ticketDetail.description}</p>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">Comments</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                      {ticketDetail.comments?.length ? ticketDetail.comments.map((c: any) => {
                        const rawType = (c.authorType || '').toLowerCase();
                        const displayAuthor = rawType === 'broker' || /broker/i.test(c.authorName) ? 'Broker' : 'Client';
                        return (
                          <div key={c.id} className="border rounded p-3 text-sm">
                            <div className="text-[11px] text-muted-foreground flex justify-between">
                              <span className="font-medium">{displayAuthor} • {format(new Date(c.createdAt), 'PP p')}</span>
                              {c.isInternal && <span className="italic">Internal</span>}
                            </div>
                            <div className="mt-1 whitespace-pre-wrap">{c.content}</div>
                          </div>
                        );
                      }) : <p className="text-xs text-muted-foreground">No comments yet</p>}
                    </div>
                    <form 
                      onSubmit={(e) => { 
                        e.preventDefault(); 
                        const fd = new FormData(e.currentTarget as HTMLFormElement); 
                        const content = String(fd.get('content') || '').trim(); 
                        if (!content) return; 
                        addCommentMutation.mutate(content); 
                        (e.currentTarget as HTMLFormElement).reset(); 
                      }} 
                      className="space-y-2"
                    >
                      <Textarea name="content" rows={3} placeholder="Add a comment as broker..." />
                      <div className="flex justify-end">
                        <Button type="submit" disabled={addCommentMutation.isPending}>
                          {addCommentMutation.isPending ? 'Posting...' : 'Post Comment'}
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>

      <Dialog open={isEmployeeDialogOpen} onOpenChange={(open) => { setIsEmployeeDialogOpen(open); if (!open) setSelectedEmployee(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Employee Details</DialogTitle>
          </DialogHeader>
          {selectedEmployee && <EmployeeDetailContent employee={selectedEmployee} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OrgTreeView({ employees, onEmployeeClick }: { employees: any[]; onEmployeeClick: (emp: any) => void }) {
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [managerAssignments, setManagerAssignments] = useState<Record<number, number | null>>({});
  const [expandedManagerIds, setExpandedManagerIds] = useState<Set<number>>(new Set());
  const hasInitializedExpanded = useRef(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const unassignedEmployees = useMemo(() => {
    return employees.filter(e => !e.managerEmployeeId);
  }, [employees]);

  const assignedEmployees = useMemo(() => {
    return employees.filter(e => e.managerEmployeeId);
  }, [employees]);

  const topLevelManagers = useMemo(() => {
    const managerIds = new Set(assignedEmployees.map(e => e.managerEmployeeId));
    return employees.filter(e => managerIds.has(e.id) && !e.managerEmployeeId);
  }, [employees, assignedEmployees]);

  useEffect(() => {
    if (hasInitializedExpanded.current) return;
    if (topLevelManagers.length === 0) return;
    setExpandedManagerIds(new Set(topLevelManagers.map(manager => manager.id)));
    hasInitializedExpanded.current = true;
  }, [topLevelManagers]);

  const getDirectReports = (managerId: number) => {
    return employees.filter(e => e.managerEmployeeId === managerId);
  };

  const updateManagerMutation = useMutation({
    mutationFn: async (assignments: { employeeId: number; managerId: number | null }[]) => {
      const promises = assignments.map(({ employeeId, managerId }) =>
        apiRequest("PATCH", `/api/employees/${employeeId}`, { managerEmployeeId: managerId })
      );
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/employees"] });
      toast({ title: "Managers assigned successfully" });
      setIsAssignModalOpen(false);
      setManagerAssignments({});
    },
    onError: (error: Error) => {
      toast({ title: "Failed to assign managers", description: error.message, variant: "destructive" });
    }
  });

  const handleAssign = () => {
    const assignments = Object.entries(managerAssignments)
      .filter(([_, managerId]) => managerId !== null)
      .map(([empId, managerId]) => ({
        employeeId: parseInt(empId),
        managerId: managerId as number
      }));
    
    if (assignments.length > 0) {
      updateManagerMutation.mutate(assignments);
    }
  };

  const OrgCard = ({ employee, isRoot = false }: { employee: any; isRoot?: boolean }) => {
    const reports = getDirectReports(employee.id);
    const hasReports = reports.length > 0;
    const isExpanded = expandedManagerIds.has(employee.id);

    const handleCardClick = () => {
      if (hasReports) {
        setExpandedManagerIds(prev => {
          const next = new Set(prev);
          if (next.has(employee.id)) {
            next.delete(employee.id);
          } else {
            next.add(employee.id);
          }
          return next;
        });
        return;
      }

      onEmployeeClick(employee);
    };
    
    return (
      <div 
        className={cn(
          "bg-white dark:bg-gray-900 border-2 rounded-lg p-4 min-w-[180px] max-w-[220px] cursor-pointer hover:shadow-lg transition-shadow",
          isRoot ? "border-slate-700 dark:border-slate-400" : "border-slate-300 dark:border-slate-600"
        )}
        onClick={handleCardClick}
        onDoubleClick={() => onEmployeeClick(employee)}
      >
        <div className="flex items-start gap-3">
          <Avatar className={cn("h-12 w-12 flex-shrink-0", isRoot ? "ring-2 ring-slate-700 dark:ring-slate-400" : "")}>
            <AvatarFallback className={cn(
              "text-base font-semibold",
              isRoot 
                ? "bg-slate-700 text-white dark:bg-slate-300 dark:text-slate-900" 
                : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
            )}>
              {employee.firstName?.[0]}{employee.lastName?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-slate-900 dark:text-slate-100 truncate">
              {employee.firstName} {employee.lastName}
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-400 truncate italic">
              {employee.position || 'No title'}
            </p>
            <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wide">Department</p>
              <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">
                {employee.department || 'Unassigned'}
              </p>
            </div>
          </div>
        </div>
        {hasReports && (
          <div className="mt-3 pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-1">
            {isExpanded ? (
              <ChevronDown className="h-3 w-3 text-slate-500" />
            ) : (
              <ChevronRight className="h-3 w-3 text-slate-500" />
            )}
            <Users className="h-3 w-3 text-slate-500" />
            <span className="text-[11px] text-slate-600 dark:text-slate-400 font-medium">{reports.length}</span>
          </div>
        )}
      </div>
    );
  };

  const TreeLevel = ({ employees: levelEmployees, isRoot = false }: { employees: any[]; isRoot?: boolean }) => {
    if (levelEmployees.length === 0) return null;
    
    return (
      <div className="flex flex-col items-center">
        <div className="flex flex-wrap justify-center gap-6">
          {levelEmployees.map((employee: any) => {
            const reports = getDirectReports(employee.id);
            const hasReports = reports.length > 0;
            const isExpanded = expandedManagerIds.has(employee.id);
            
            return (
              <div key={employee.id} className="flex flex-col items-center">
                <OrgCard employee={employee} isRoot={isRoot && levelEmployees.length === 1} />
                
                {hasReports && isExpanded && (
                  <>
                    <div className="w-0.5 h-8 bg-slate-300 dark:bg-slate-600" />
                    
                    {reports.length > 1 && (
                      <div 
                        className="h-0.5 bg-slate-300 dark:bg-slate-600" 
                        style={{ 
                          width: `${Math.min(reports.length * 200, 800)}px`,
                          maxWidth: '90vw'
                        }}
                      />
                    )}
                    
                    <div className="flex flex-wrap justify-center gap-4 mt-0">
                      {reports.map((report: any) => {
                        const subReports = getDirectReports(report.id);
                        const hasSubReports = subReports.length > 0;
                        const isSubExpanded = expandedManagerIds.has(report.id);
                        
                        return (
                          <div key={report.id} className="flex flex-col items-center">
                            <div className="w-0.5 h-8 bg-slate-300 dark:bg-slate-600" />
                            <OrgCard employee={report} />
                            
                            {hasSubReports && isSubExpanded && (
                              <>
                                <div className="w-0.5 h-6 bg-slate-300 dark:bg-slate-600" />
                                
                                {subReports.length > 1 && (
                                  <div 
                                    className="h-0.5 bg-slate-300 dark:bg-slate-600" 
                                    style={{ 
                                      width: `${Math.min(subReports.length * 180, 600)}px`,
                                      maxWidth: '80vw'
                                    }}
                                  />
                                )}
                                
                                <div className="flex flex-wrap justify-center gap-3 mt-0">
                                  {subReports.map((sub: any) => (
                                    <div key={sub.id} className="flex flex-col items-center">
                                      <div className="w-0.5 h-6 bg-slate-300 dark:bg-slate-600" />
                                      <OrgCard employee={sub} />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-4 text-sm">
        <span className="text-muted-foreground">{unassignedEmployees.length} unassigned</span>
        <Button 
          variant="link" 
          className="text-teal-600 hover:text-teal-700 p-0 h-auto font-medium"
          onClick={() => setIsAssignModalOpen(true)}
        >
          Assign
        </Button>
        <p className="text-xs text-muted-foreground">Unassigned employees will not be shown</p>
      </div>

      {topLevelManagers.length === 0 && assignedEmployees.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">No organizational hierarchy data available</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Use the Assign button above to set up manager relationships</p>
        </div>
      ) : (
        <div className="overflow-x-auto pb-8">
          <div className="min-w-fit">
            <TreeLevel employees={topLevelManagers} isRoot={true} />
          </div>
        </div>
      )}

      <Dialog open={isAssignModalOpen} onOpenChange={setIsAssignModalOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold">Org Chart Members</DialogTitle>
            </div>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-medium">Select members manager</p>
              <span className="text-sm text-muted-foreground">{unassignedEmployees.length} unassigned</span>
            </div>

            {unassignedEmployees.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">All employees have managers assigned</p>
            ) : (
              <div className="space-y-3">
                {unassignedEmployees.map((emp: any) => (
                  <div key={emp.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                    <Avatar className="h-10 w-10 flex-shrink-0">
                      <AvatarFallback className="bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200 font-semibold">
                        {emp.firstName?.[0]}{emp.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{emp.firstName} {emp.lastName} <span className="font-normal text-muted-foreground">reports to</span></p>
                      <Select
                        value={managerAssignments[emp.id]?.toString() || ""}
                        onValueChange={(val) => setManagerAssignments(prev => ({
                          ...prev,
                          [emp.id]: val ? parseInt(val) : null
                        }))}
                      >
                        <SelectTrigger className="mt-1 h-8">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees
                            .filter(e => e.id !== emp.id)
                            .map((manager: any) => (
                              <SelectItem key={manager.id} value={manager.id.toString()}>
                                <div className="flex flex-col">
                                  <span>{manager.firstName} {manager.lastName}</span>
                                  {(manager.position || manager.department) && (
                                    <span className="text-xs text-muted-foreground">
                                      {[manager.position, manager.department].filter(Boolean).join(" · ")}
                                    </span>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-center pt-4">
              <Button 
                onClick={handleAssign}
                disabled={Object.keys(managerAssignments).length === 0 || updateManagerMutation.isPending}
                className="bg-teal-600 hover:bg-teal-700 text-white px-8"
              >
                {updateManagerMutation.isPending ? "Assigning..." : "Assign"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmployeeDetailContent({ employee }: { employee: any }) {
  const milestone = employee.yearsOfService >= 5 ? (
    employee.yearsOfService >= 20 ? { label: "20+ Years", color: "bg-yellow-500", icon: "🏆" } :
    employee.yearsOfService >= 15 ? { label: "15 Years", color: "bg-purple-500", icon: "💎" } :
    employee.yearsOfService >= 10 ? { label: "10 Years", color: "bg-blue-500", icon: "⭐" } :
    { label: "5 Years", color: "bg-green-500", icon: "🎯" }
  ) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Avatar className="h-20 w-20">
          {employee.profilePicture && <AvatarImage src={employee.profilePicture} />}
          <AvatarFallback className="text-lg">
            {employee.firstName?.[0]}{employee.lastName?.[0]}
          </AvatarFallback>
        </Avatar>
        <div>
          <h2 className="text-2xl font-bold">
            {employee.firstName} {employee.lastName}
          </h2>
          <p className="text-lg text-muted-foreground">{employee.position}</p>
          <div className="flex items-center gap-2 mt-1">
            {employee.employmentType && (
              <Badge variant="secondary">{employee.employmentType}</Badge>
            )}
            {milestone && (
              <Badge className={cn("text-white", milestone.color)}>
                {milestone.icon} {milestone.label}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-3">
          <h3 className="font-semibold">Contact Information</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span>{employee.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span>{employee.phone || '—'}</span>
            </div>
            {(employee.city || employee.state) && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{employee.city}{employee.city && employee.state ? ', ' : ''}{employee.state}</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">Employment Details</h3>
          <div className="space-y-2 text-sm">
            <div><span className="font-medium">Department:</span> {departmentLabel(employee.department)}</div>
            {employee.hireDate && (
              <div><span className="font-medium">Hire Date:</span> {format(employee.hireDate, 'MMM dd, yyyy')}</div>
            )}
            <div><span className="font-medium">Years of Service:</span> {employee.yearsOfService}</div>
            {employee.managerEmployeeId && (
              <div><span className="font-medium">Manager ID:</span> {employee.managerEmployeeId}</div>
            )}
          </div>
        </div>
      </div>

      {(employee.skills?.length > 0 || employee.performanceRating) && (
        <div className="space-y-3">
          <h3 className="font-semibold">Skills & Performance</h3>
          {employee.skills?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {employee.skills.map((skill: string) => (
                <Badge key={skill} variant="outline">{skill}</Badge>
              ))}
            </div>
          )}
          <div className="text-sm"><span className="font-medium">Performance Rating:</span> {employee.performanceRating}</div>
          {employee.lastReviewDate && (
            <div className="text-sm"><span className="font-medium">Last Review:</span> {format(employee.lastReviewDate, 'MMM dd, yyyy')}</div>
          )}
        </div>
      )}
    </div>
  );
}
